/* RYZE 100 ml offer, quick add.
 *
 * Trust model, in one place so it cannot drift:
 *
 *  1. This script NEVER decides what anything costs, and contains no price and no
 *     discount rate. Both figures on a card were rendered server side by Liquid
 *     from the variant Shopify actually sells. Nothing here reads or writes them.
 *  2. It only ever handles a NUMERIC variant id and an integer quantity, both
 *     taken from the server-rendered card, and posts them to Shopify's own
 *     /cart/add.js. No price is ever transmitted.
 *  3. Every string it puts on the page is either a literal from this file or
 *     Shopify's own error description, and all of it goes through textContent.
 *     No innerHTML anywhere. That is now enforced rather than assumed: only an
 *     Error this file raised carries the ryzeSafe flag, so a parser message
 *     ("... is not valid JSON") or a network one ("Failed to fetch", "Load
 *     failed") can never be printed on a card as if it were copy.
 *  4. The add and everything after it are SEPARATE outcomes. Once /cart/add.js
 *     answers ok the bottle IS in the cart, and the customer is told so right
 *     there. A follow-up /cart.js read that fails costs the badge and the
 *     publish, never the success message: reporting a completed add as failed
 *     is how a customer ends up buying the same bottle twice.
 *  5. After a successful add the real cart is read back from /cart.js and that
 *     response, not the add response, is what the header badge and every
 *     subscriber are updated from. The add response is not uniform across
 *     publishers on this theme and must not be trusted.
 *  6. Transient unavailability is `aria-disabled`, never the `disabled`
 *     property. Per spec a focused element that becomes disabled is unfocused,
 *     which drops a keyboard, switch or screen-reader user at the top of a long
 *     homepage in the middle of their interaction. The real `disabled`
 *     attribute is reserved for the server-rendered sold-out case, which is
 *     never toggled while focused. This is the split Dawn already uses in
 *     assets/product-form.js, not a third pattern.
 *
 * Bound by delegation from the section root, and re-bound on
 * shopify:section:load, so it survives a section re-render in the theme editor.
 */

(function () {
  'use strict';

  if (window.__ryzeOfferInit) return;
  window.__ryzeOfferInit = true;

  var ROOT_SELECTOR = '[data-ryze-offer]';
  var INERT_ATTR = 'aria-disabled';

  function routeRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  /* ---------- availability ----------

     Two different states, two different attributes, and mixing them is the bug
     this section shipped with:

       disabled       server rendered, sold out, permanent for the life of the
                      page. Never toggled by this file, so it can never pull
                      focus out from under anybody.
       aria-disabled  transient. The request window on the CTAs, the floor and
                      the ceiling on the stepper. The control stays focusable
                      and stays in the tab order; the stylesheet makes it look
                      and behave disabled, and the click delegate below refuses
                      to act on it. */

  function setInert(el, inert) {
    if (inert) {
      el.setAttribute(INERT_ATTR, 'true');
    } else {
      el.removeAttribute(INERT_ATTR);
    }
  }

  function isInert(el) {
    return el.hasAttribute('disabled') || el.getAttribute(INERT_ATTR) === 'true';
  }

  function isSoldOut(card) {
    return card.classList.contains('is-unavailable');
  }

  /* Only an error raised in this file is quotable to a customer. */
  function safeError(message) {
    var err = new Error(message);
    err.ryzeSafe = true;
    return err;
  }

  /* ---------- quantity ---------- */

  /* Bounds come from the input's own min and max attributes, which Liquid wrote,
     so changing the cap is a schema edit rather than a code edit. */
  function clampQty(input) {
    var min = parseInt(input.getAttribute('min'), 10);
    var max = parseInt(input.getAttribute('max'), 10);
    if (!isFinite(min)) min = 1;
    if (!isFinite(max)) max = 10;

    var n = parseInt(input.value, 10);
    if (!isFinite(n)) n = min;
    if (n < min) n = min;
    if (n > max) n = max;

    input.value = String(n);
    return { value: n, min: min, max: max };
  }

  /* The minus is inert at the floor and the plus at the ceiling, so the stepper
     can never produce a value the input would then have to correct. Inert, not
     disabled: the edge button is very often the one the user is standing on. */
  function syncSteppers(card) {
    var input = card.querySelector('[data-offer-qty]');
    if (!input) return;

    var state = clampQty(input);

    // A sold-out card was disabled by Liquid because the variant cannot be
    // bought, and that is not this script's to undo. Under the EG-only geo gate
    // that is every card seen from abroad.
    if (isSoldOut(card)) return;

    Array.prototype.forEach.call(card.querySelectorAll('[data-offer-step]'), function (btn) {
      var delta = Number(btn.getAttribute('data-offer-step'));
      var atEdge = delta < 0 ? state.value <= state.min : state.value >= state.max;
      // The minus ships from Liquid with the real disabled attribute, because a
      // card opens at the floor and that is the correct no-JS state. The first
      // sync hands it over to aria-disabled, after which nothing here ever
      // touches the property again.
      btn.removeAttribute('disabled');
      setInert(btn, atEdge);
    });
  }

  /* ---------- messages ---------- */

  function setMessage(card, text, kind) {
    var el = card.querySelector('.ryze-100ml-card__msg');
    if (!el) return;
    el.textContent = text || '';
    el.className = 'ryze-100ml-card__msg' + (kind ? ' ryze-100ml-card__msg--' + kind : '');
    /* THE STILL CARD (Sawy 30 Oct 2026): the line is heard, never drawn in the card (a drawn line grew the card
       under the customer's finger); an error is SEEN in the house toast, as "added" already is. */
    if (kind === 'error' && text && window.RyzeToast && typeof window.RyzeToast.show === 'function') {
      window.RyzeToast.show(text, 'error');
    }
  }

  /* ---------- cart ---------- */

  function updateHeaderBadge(cart) {
    Array.prototype.forEach.call(document.querySelectorAll('.ryze-nav__cart-count'), function (b) {
      b.textContent = cart.item_count;
      b.style.display = cart.item_count > 0 ? 'flex' : 'none';
      /* Bump ONLY on a real count change, and NEVER during the first second
         after boot: navigations write every badge fresh (the marker starts
         empty on a new page), and those load-time writes must initialise
         silently. The boot anchor is shared: whichever script writes first
         plants it, so all writers count from the same moment. */
      window.__ryzeBadgeBoot = window.__ryzeBadgeBoot || Date.now();
      var ryzeBadgeMoved = b.dataset.ryzeLastCount !== String(b.textContent);
      b.dataset.ryzeLastCount = String(b.textContent);
      if (ryzeBadgeMoved && Date.now() - window.__ryzeBadgeBoot > 1000) {
        b.classList.remove('ryze-badge-bump');
        void b.offsetWidth;
        b.classList.add('ryze-badge-bump');
      }
    });
  }

  function readCart() {
    return fetch(routeRoot() + 'cart.js', {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }).then(function (r) {
      // Status before parse. Shopify throttles cart endpoints per IP and answers
      // text/plain, which would otherwise reject inside r.json() and be
      // indistinguishable from a real failure. The caller treats anything that
      // goes wrong here as "no cart", never as a failed add.
      if (!r.ok) throw safeError('Cart read failed.');
      return r.json();
    });
  }

  function setBusy(card, btn, busy) {
    if (btn) {
      if (busy) {
        btn.classList.add('is-busy');
      } else {
        btn.classList.remove('is-busy');
      }
    }

    // Nothing to arm or disarm on a sold-out card: Liquid already disabled every
    // control on it, permanently and correctly.
    if (isSoldOut(card)) return;

    // Both CTAs and the whole stepper go inert together for the duration, so a
    // second click cannot start a second request against the same card. They go
    // aria-disabled rather than disabled precisely because one of them is the
    // element the user just activated and still has focus on.
    Array.prototype.forEach.call(
      card.querySelectorAll('[data-offer-add], [data-offer-buy], [data-offer-step]'),
      function (el) {
        setInert(el, busy);
      }
    );

    // A number field cannot be made inert by an ARIA attribute alone, and
    // disabling it would blur it exactly like the buttons. readonly is the
    // focus-preserving equivalent: the value cannot change, the caret stays.
    var input = card.querySelector('[data-offer-qty]');
    if (input) {
      input.readOnly = busy;
      setInert(input, busy);
    }

    if (!busy) syncSteppers(card);
  }

  function release(card, btn) {
    card.dataset.offerBusy = '';
    setBusy(card, btn, false);
  }

  function submit(card, btn, goToCheckout) {
    if (card.dataset.offerBusy === '1') return;

    var id = Number(card.getAttribute('data-variant-id'));
    if (!isFinite(id) || id <= 0) return;

    var input = card.querySelector('[data-offer-qty]');
    var qty = input ? clampQty(input).value : 1;

    card.dataset.offerBusy = '1';
    setBusy(card, btn, true);
    setMessage(card, '', null);

    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: qty }] }),
    })
      .then(function (res) {
        // The STATUS decides the outcome, never the parse. The body is still
        // read on the way past, because a 422 carries Shopify's own reason in
        // `description` and that is the only useful thing to show a customer,
        // but a body that will not parse (a throttled text/plain reply, a 5xx
        // HTML interstitial) is turned into null rather than into a rejection
        // carrying the browser's internal wording.
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            if (!res.ok) {
              throw safeError(body && body.description ? body.description : 'Could not add to cart.');
            }
            return body;
          });
      })
      .then(function () {
        // Past this line the add SUCCEEDED and the bottle is in the cart.
        // Everything below is presentation, and none of it may be reported as a
        // failed add.
        if (goToCheckout) {
          // Redirect only on the success path, and only from inside it. Doing
          // this in a finally would send a customer to checkout after a FAILED
          // add. Nothing on this page needs updating first, and the button is
          // deliberately left in its pending state until the navigation lands.
          // CODE-05 (2026-10-18): a bfcache back from checkout releases it.
          // MSR-01: the pixels' AddToCart leaves first (1.5 s, bounded).
          window.addEventListener('pageshow', function (e) {
            if (e.persisted) release(card, btn);
          }, { once: true });
          setTimeout(function () {
            window.location.href = routeRoot() + 'checkout';
          }, 0); // MSR-01 hold parked at 0 for the owner's call (1500 = the measured pixel wait)
          return null;
        }

        // Success is confirmed to the customer here, BEFORE the follow-up read,
        // so a throttled or dropped /cart.js can no longer paint a completed add
        // as an error and invite a duplicate purchase.
        var title = card.getAttribute('data-title') || '';
        if (window.RyzeToast && typeof window.RyzeToast.show === 'function') {
          window.RyzeToast.show(title, 'added');
        }
        setMessage(card, 'Added to your cart.', 'ok');
        release(card, btn);

        // Best effort from here down. A read that fails resolves to null and the
        // add stays a success.
        return readCart().catch(function () {
          return null;
        });
      })
      .then(function (cart) {
        if (!cart) return;

        updateHeaderBadge(cart);

        // A sibling upsell popup subscribes to this, and the theme's own cart
        // surfaces do too. The real /cart.js response is published, never the
        // add response, because cartUpdate payloads are not uniform across
        // publishers on this theme and a subscriber cannot re-derive item_count
        // from the add response. That is also why this stays inside the
        // cart-is-known branch: publishing without a payload would hand
        // subscribers a shape they do not accept.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'ryze-100ml-offer',
            cartData: cart,
          });
        }
      })
      .catch(function (err) {
        // Only wording this file wrote, or Shopify's own description, reaches a
        // card. Anything else is browser internals and gets the house sentence.
        setMessage(
          card,
          err && err.ryzeSafe && err.message ? err.message : 'Something went wrong. Please try again.',
          'error'
        );
        release(card, btn);
      });
  }

  /* ---------- binding ---------- */

  function bind(root) {
    if (root.dataset.offerReady === '1') return;
    root.dataset.offerReady = '1';

    root.addEventListener('click', function (e) {
      // An SVG inside a button is a legitimate event target and does implement
      // closest, but a text or document node does not, so this is guarded.
      if (!e.target || typeof e.target.closest !== 'function') return;

      // An aria-disabled control keeps its focus and its place in the tab order,
      // which is the whole point of it, so it still receives a click when it is
      // activated from the keyboard. The refusal lives here, exactly as it does
      // in Dawn's own product-form.js.

      var step = e.target.closest('[data-offer-step]');
      if (step && root.contains(step)) {
        if (isInert(step)) return;
        var stepCard = step.closest('[data-offer-card]');
        if (!stepCard) return;
        var input = stepCard.querySelector('[data-offer-qty]');
        if (!input) return;
        var delta = Number(step.getAttribute('data-offer-step')) || 0;
        input.value = String((parseInt(input.value, 10) || 1) + delta);
        syncSteppers(stepCard);
        return;
      }

      var add = e.target.closest('[data-offer-add]');
      if (add && root.contains(add)) {
        if (isInert(add)) return;
        var addCard = add.closest('[data-offer-card]');
        if (addCard) submit(addCard, add, false);
        return;
      }

      var buy = e.target.closest('[data-offer-buy]');
      if (buy && root.contains(buy)) {
        if (isInert(buy)) return;
        var buyCard = buy.closest('[data-offer-card]');
        if (buyCard) submit(buyCard, buy, true);
      }
    });

    // A typed value is clamped when the field is left rather than on every
    // keystroke, so "10" is not rewritten to "1" the moment the 1 is typed.
    root.addEventListener('change', function (e) {
      if (!e.target || typeof e.target.closest !== 'function') return;
      var input = e.target.closest('[data-offer-qty]');
      if (!input || !root.contains(input)) return;
      var card = input.closest('[data-offer-card]');
      if (card) syncSteppers(card);
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-offer-card]'), function (card) {
      syncSteppers(card);
    });

    setupFilm(root);
  }

  /* The artwork film. Same arrangement as the duo section's, for the same
     reasons: preload="none" with NO src, and the source is attached only when
     an IntersectionObserver says the section is close, so a visitor who never
     scrolls this far pays for the poster and not the film. The loop seam is
     solved in the FILE (it plays forward then in reverse, so every joint is an
     ordinary adjacent-frame step); the script only starts and stops it. Under
     prefers-reduced-motion it is never started and the poster stands in. */
  function setupFilm(root) {
    var film = root.querySelector('.ryze-100ml__art');
    if (!film || film.tagName !== 'VIDEO') return;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Without IntersectionObserver the film is simply never fetched and the
    // poster stays, which is the cheaper of the two failures.
    if (!('IntersectionObserver' in window)) return;

    var fetched = false;
    var onScreen = false;

    var start = function () {
      if (!fetched) {
        fetched = true;
        var src = film.dataset.src;
        if (!src) return;
        film.setAttribute('src', src);
        film.load();
      }
      var p = film.play();
      // Autoplay can be refused; the poster is already painted, so a refusal
      // simply leaves the still and is not worth surfacing.
      if (p && typeof p.catch === 'function') p.catch(function () {});
    };

    var stop = function () {
      if (!film.paused) film.pause();
    };

    // If the file cannot be decoded, return the element to its pre-play state:
    // no source, poster showing, never an empty box. It cannot re-enter,
    // because `fetched` stays true and a source-less element raises no error.
    film.addEventListener('error', function () {
      film.removeAttribute('src');
      film.load();
    });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          onScreen = e.isIntersecting;
          if (e.isIntersecting) {
            start();
          } else {
            stop();
          }
        });
      },
      // Enough margin to have the file in hand by the time the section arrives,
      // not so much that scrolling past the hero already pays for it.
      { rootMargin: '200px 0px' }
    );
    io.observe(film);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else if (onScreen) {
        start();
      }
    });
  }

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    if (context.matches && context.matches(ROOT_SELECTOR)) {
      bind(context);
      return;
    }
    Array.prototype.forEach.call(context.querySelectorAll(ROOT_SELECTOR), bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // The theme editor replaces the whole section node, so the listeners bound
  // above are thrown away with the old node and the new one needs its own.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });

  /* ---------- THE RETURN SLOT (100 ml): notify capture ---------- */
  /* Same law as ryze-pcard: NATIVE post only (scripted POSTs are
     spam-binned), sessionStorage done-marker, aria-disabled busy. */
  document.addEventListener('submit', function (e) {
    var form = e.target && e.target.classList && e.target.classList.contains('ryze-100ml-notify') ? e.target : null;
    if (!form) return;
    var card = form.closest('.ryze-100ml-card');
    var rt = form.querySelector('[data-offer-return-to]');
    if (rt) rt.value = window.location.pathname + window.location.search;
    try {
      sessionStorage.setItem('ryze_notified', JSON.stringify({
        h: card ? card.getAttribute('data-notify-handle') : '',
        s: '100ml', /* CODE-27: the surface, so a pcard of the same scent never takes it */
        t: Date.now()
      }));
    } catch (err) {}
    var btn = form.querySelector('[data-offer-notify]');
    if (btn) btn.setAttribute('aria-disabled', 'true');
  });

  (function () {
    var raw = null;
    try { raw = sessionStorage.getItem('ryze_notified'); } catch (err) {}
    if (!raw) return;
    var mark = null;
    try { mark = JSON.parse(raw); } catch (err) {}
    if (!mark || !mark.h || (Date.now() - (mark.t || 0)) > 600000) return;
    if (mark.s && mark.s !== '100ml') return; /* a pcard's marker */
    var card = document.querySelector('.ryze-100ml-card[data-notify-handle="' + mark.h + '"]');
    if (!card) return; /* not this surface; ryze-pcard.js may own it */
    try { sessionStorage.removeItem('ryze_notified'); } catch (err) {}
    var form = card.querySelector('.ryze-100ml-notify');
    if (form) form.classList.add('is-done');
    var done = form && form.querySelector('.ryze-100ml-notify__done');
    if (done) { done.setAttribute('tabindex', '-1'); done.focus({ preventScroll: true }); }
    setTimeout(function () { card.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, 300);
  })();
})();
